import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

// Needed to pin timestamps to a fixed offset (India, +05:30) regardless of the viewer's TZ.
dayjs.extend(utc);

/** India Standard Time offset, in minutes. */
const IST_OFFSET = 330;
/** Format a timestamp in India time (IST) — independent of the browser's timezone. */
const formatIst = (iso?: string | null) =>
  iso ? dayjs(iso).utcOffset(IST_OFFSET).format('MMM D • h:mm A') : '';

/** The kind of record comments/logs attach to (e.g. "Task", "DailyDirection"). */
export type EntityType = 'Task' | 'Engagement' | 'DailyDirection' | string;

/** A comment on an entity (fields read defensively via the accessors below). */
export interface Comment {
  id: number;
  entity_type?: string;
  entity_id?: number;
  user?: number | { id?: number; full_name?: string; name?: string } | null;
  user_name?: string | null;
  /** Comment body — the API uses `text` (older responses used `comment`). */
  text?: string | null;
  comment?: string | null;
  created_at?: string | null;
}

/** A file attached to an entity, via /activity/attachment/. */
export interface Attachment {
  id: number;
  entity_type?: string;
  entity_id?: number;
  file_url?: string | null;
  file_type?: string | null;
  uploaded_by?: number | { id?: number; full_name?: string; name?: string } | null;
  created_at?: string | null;
}

/** An assignee on an entity, via /activity/assignee/. */
export interface Assignee {
  id: number;
  entity_type?: string;
  entity_id?: number;
  user?: number | { id?: number; full_name?: string; name?: string } | null;
  user_name?: string | null;
}

/** A change-log entry for an entity. */
export interface ActivityLog {
  id: number;
  entity_type?: string;
  entity_id?: number;
  user?: number | { id?: number; full_name?: string; name?: string } | null;
  user_name?: string | null;
  action?: string | null;
  old_value?: string | null;
  new_value?: string | null;
  created_at?: string | null;
}

export interface CreateCommentPayload {
  entity_type: EntityType;
  entity_id: number;
  user: number;
  text: string;
}

export interface CreateAttachmentPayload {
  entity_type: EntityType;
  entity_id: number;
  file_url: string;
  file_type?: string;
  uploaded_by: number;
}

export interface CreateLogPayload {
  entity_type: EntityType;
  entity_id: number;
  user: number;
  action: string;
  old_value?: string;
  new_value?: string;
}

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/* ------------------------------------------------------------------ *
 * Defensive accessors.
 * ------------------------------------------------------------------ */
const personName = (
  user: number | { id?: number; full_name?: string; name?: string } | null | undefined,
  fallbackName?: string | null,
): string => {
  if (fallbackName) return fallbackName;
  if (user && typeof user === 'object') return user.full_name || user.name || '';
  return '';
};

export const commentUserName = (c: Comment) => personName(c.user, c.user_name) || 'User';
export const commentText = (c: Comment) => c.text || c.comment || '';
export const commentWhen = (c: Comment) => formatIst(c.created_at);

/** File name from an attachment's URL (falls back to the type). */
export const attachmentName = (a: Attachment): string => {
  if (a.file_url) {
    const clean = a.file_url.split('?')[0];
    const last = clean.substring(clean.lastIndexOf('/') + 1);
    if (last) return decodeURIComponent(last);
  }
  return a.file_type ? `file.${a.file_type}` : 'Attachment';
};
export const assigneeName = (a: Assignee) => personName(a.user, a.user_name) || 'User';

export const logUserName = (l: ActivityLog) => personName(l.user, l.user_name);
export const logWhen = (l: ActivityLog) => formatIst(l.created_at);
/** A human title for a log action, e.g. "status_changed" → "Status Changed". */
export const logTitle = (l: ActivityLog) =>
  (l.action || 'Updated').replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
/** A subtitle describing the change (old → new) plus who did it. */
export const logDetail = (l: ActivityLog): string => {
  const who = logUserName(l);
  if (l.old_value && l.new_value) {
    const by = who ? ` by ${who}` : '';
    return `${l.old_value} → ${l.new_value}${by}`;
  }
  return who ? `By ${who}` : '';
};

export const activityApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getComments: build.query<Comment[], { entityType: EntityType; entityId: number | string }>({
      query: ({ entityType, entityId }) => ({
        url: `${ApiEndPoints.activityComment}/`,
        params: { entity_type: entityType, entity_id: entityId },
      }),
      transformResponse: (r: Listish<Comment>) => asList(r),
      providesTags: ['Comments'],
    }),
    createComment: build.mutation<Comment, CreateCommentPayload>({
      query: (body) => ({ url: `${ApiEndPoints.activityComment}/`, method: 'POST', body }),
      invalidatesTags: ['Comments'],
    }),
    getAttachments: build.query<Attachment[], { entityType: EntityType; entityId: number | string }>({
      query: ({ entityType, entityId }) => ({
        url: `${ApiEndPoints.activityAttachment}/`,
        params: { entity_type: entityType, entity_id: entityId },
      }),
      transformResponse: (r: Listish<Attachment>) => asList(r),
      providesTags: ['Attachments'],
    }),
    createAttachment: build.mutation<Attachment, CreateAttachmentPayload>({
      query: (body) => ({ url: `${ApiEndPoints.activityAttachment}/`, method: 'POST', body }),
      invalidatesTags: ['Attachments'],
    }),
    getAssignees: build.query<Assignee[], { entityType: EntityType; entityId: number | string }>({
      query: ({ entityType, entityId }) => ({
        url: `${ApiEndPoints.activityAssignee}/`,
        params: { entity_type: entityType, entity_id: entityId },
      }),
      transformResponse: (r: Listish<Assignee>) => asList(r),
      providesTags: ['Assignees'],
    }),
    getActivityLog: build.query<ActivityLog[], { entityType: EntityType; entityId: number | string }>({
      query: ({ entityType, entityId }) => ({
        url: `${ApiEndPoints.activityLog}/`,
        params: { entity_type: entityType, entity_id: entityId },
      }),
      transformResponse: (r: Listish<ActivityLog>) => asList(r),
      providesTags: ['ActivityLog'],
    }),
    createActivityLog: build.mutation<ActivityLog, CreateLogPayload>({
      query: (body) => ({ url: `${ApiEndPoints.activityLog}/`, method: 'POST', body }),
      invalidatesTags: ['ActivityLog'],
    }),
  }),
});

export const {
  useGetCommentsQuery,
  useCreateCommentMutation,
  useGetAttachmentsQuery,
  useCreateAttachmentMutation,
  useGetAssigneesQuery,
  useGetActivityLogQuery,
  useCreateActivityLogMutation,
} = activityApi;
